// frontend/src/screens/admin/sps/RunCalculatorScreen.jsx
import { useState, useRef, useEffect } from 'react';
import {
  Container, Row, Col, Button, Card, Alert, Badge,
  ProgressBar, Form, Spinner,
} from 'react-bootstrap';
import { useRunIndividualMutation } from '../../../slices/spsApiSlices';
import { useGetStaffDirectoryQuery } from '../../../slices/spsApiSlices';

// ── API base URL — works in dev (port 5800) and production (same host) ──────
const API_BASE = window.location.hostname === 'localhost'
  ? 'http://localhost:5800'
  : '';

// ── Individual run card ───────────────────────────────────────────────────────
function IndividualRunCard() {
  const [ppsInput, setPpsInput]     = useState('');
  const [runOne, { isLoading }]     = useRunIndividualMutation();
  const [result,  setResult]        = useState(null);
  const [error,   setError]         = useState(null);

  const handleRun = async () => {
    const pps = ppsInput.trim().toUpperCase();
    if (!pps) return;
    setResult(null);
    setError(null);
    try {
      const res = await runOne(pps).unwrap();
      setResult(res);
    } catch (err) {
      setError(err?.data?.message || 'Calculator run failed');
    }
  };

  return (
    <Card className="mb-4">
      <Card.Header className="fw-semibold bg-light">
        Run for One Staff Member
      </Card.Header>
      <Card.Body>
        <p className="text-muted small mb-3">
          Enter a PPS number to generate the three output files for that person only
          (Excel cert + CPI summary HTML + Annual Benefit Statement HTML).
          Takes approximately 20–30 seconds.
        </p>
        <Row className="align-items-end g-2">
          <Col xs={12} sm={5} md={4}>
            <Form.Label className="fw-semibold">PPS Number</Form.Label>
            <Form.Control
              type="text"
              placeholder="e.g. 1205726A"
              value={ppsInput}
              onChange={(e) => setPpsInput(e.target.value.toUpperCase())}
              onKeyDown={(e) => e.key === 'Enter' && handleRun()}
              style={{ fontFamily: 'monospace' }}
            />
          </Col>
          <Col xs="auto">
            <Button
              variant="primary"
              onClick={handleRun}
              disabled={isLoading || !ppsInput.trim()}
            >
              {isLoading
                ? <><Spinner size="sm" animation="border" className="me-1" /> Running…</>
                : '▶ Run Calculator'}
            </Button>
          </Col>
        </Row>

        {error && <Alert variant="danger" className="mt-3 mb-0">{error}</Alert>}

        {result && (
          <Alert variant="success" className="mt-3 mb-0">
            <strong>✓ {result.staffName}</strong> — certificate generated for {result.runYear}
            <div className="mt-2 d-flex gap-2 flex-wrap">
              <a
                href={`${API_BASE}/api/calculator/download/${result.runYear}/${result.files.xlsx}`}
                target="_blank"
                rel="noreferrer"
                className="btn btn-sm btn-outline-success"
              >
                ⬇ Excel Certificate
              </a>
              <a
                href={`${API_BASE}/api/calculator/download/${result.runYear}/${result.files.absHtml}`}
                target="_blank"
                rel="noreferrer"
                className="btn btn-sm btn-outline-primary"
              >
                ⬇ Annual Benefit Statement
              </a>
              <a
                href={`${API_BASE}/api/calculator/download/${result.runYear}/${result.files.cpiHtml}`}
                target="_blank"
                rel="noreferrer"
                className="btn btn-sm btn-outline-secondary"
              >
                ⬇ CPI Summary
              </a>
            </div>
          </Alert>
        )}
      </Card.Body>
    </Card>
  );
}


// ── Batch run card with SSE progress ─────────────────────────────────────────
// ── Batch run card with SSE progress ─────────────────────────────────────────
function BatchRunCard() {
  const [status,    setStatus]    = useState('idle');   // idle | running | done | error
  const [progress,  setProgress]  = useState({ processed: 0, total: 0, pct: 0 });
  const [log,       setLog]       = useState([]);
  const [runId,     setRunId]     = useState(null);
  const [confirmed, setConfirmed] = useState(false);
  const [runMode,   setRunMode]   = useState('resume');  // 'resume' | 'fresh'
  const [checkpointInfo, setCheckpointInfo] = useState(null); // { completed, failed } | null
  const [checkpointLoading, setCheckpointLoading] = useState(true);
  const logRef  = useRef(null);
  const evsRef  = useRef(null);

  // Auto-scroll log
  useEffect(() => {
    if (logRef.current) {
      logRef.current.scrollTop = logRef.current.scrollHeight;
    }
  }, [log]);

  // Cleanup SSE on unmount
  useEffect(() => () => { if (evsRef.current) evsRef.current.close(); }, []);

  // Look up whether a checkpoint already exists for this run year, so we
  // can show "Resume (1,204 already done)" instead of a blind toggle.
  useEffect(() => {
    let cancelled = false;
    setCheckpointLoading(true);
    fetch(`${API_BASE}/api/calculator/checkpoint-status`, { credentials: 'include' })
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (!cancelled) {
          setCheckpointInfo(data || null);
          // If nothing has run yet, default to fresh — "resume" doesn't mean anything yet.
          if (!data || (data.completed === 0 && data.failed === 0)) {
            setRunMode('fresh');
          }
        }
      })
      .catch(() => { if (!cancelled) setCheckpointInfo(null); })
      .finally(() => { if (!cancelled) setCheckpointLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const appendLog = (line, type = 'log') => {
    setLog((prev) => [...prev.slice(-300), { line, type }]); // keep last 300 lines
  };

  const startBatch = () => {
    setLog([]);
    setProgress({ processed: 0, total: 0, pct: 0 });
    setStatus('running');
    setRunId(null);

    const controller = new AbortController();

    fetch(`${API_BASE}/api/calculator/run-all`, {
      method:  'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-csrf-token': document.cookie
          .split('; ')
          .find((r) => r.startsWith('csrf-token='))
          ?.split('=')[1] || '',
      },
      credentials: 'include',
      signal: controller.signal,
      body: JSON.stringify({ fresh: runMode === 'fresh' }),
    })
      .then(async (res) => {
        if (!res.ok || !res.body) {
          throw new Error(`HTTP ${res.status}`);
        }
        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });

          const lines = buffer.split('\n');
          buffer = lines.pop();

          for (const line of lines) {
            if (!line.startsWith('data: ')) continue;
            try {
              const event = JSON.parse(line.slice(6));
              if (event.type === 'progress') {
                setProgress({ processed: event.processed, total: event.total, pct: event.pct });
                appendLog(event.line, 'progress');
              } else if (event.type === 'error') {
                appendLog(event.line, 'error');
              } else if (event.type === 'done') {
                setStatus(event.status === 'completed' ? 'done' : 'error');
                setRunId(event.runId);
              } else if (event.type === 'start') {
                setRunId(event.runId);
                appendLog(event.message, 'start');
              } else {
                appendLog(event.line || JSON.stringify(event), 'log');
              }
            } catch (_) { /* non-JSON line, ignore */ }
          }
        }
      })
      .catch((err) => {
        if (err.name !== 'AbortError') {
          appendLog(`Stream interrupted: ${err.message} (check server logs)`, 'error');
          setStatus('error');
        }
      });

    evsRef.current = { close: () => controller.abort() };
  };

  const stopRun = () => {
    if (evsRef.current) evsRef.current.close();
    setStatus('idle');
  };

  const logLineStyle = (type) => ({
    color:      type === 'error'    ? '#dc3545'
              : type === 'progress' ? '#0d6efd'
              : type === 'start'    ? '#198754'
              : '#212529',
    fontFamily: 'monospace',
    fontSize:   '0.78rem',
    lineHeight: 1.4,
  });

  const hasCheckpoint = checkpointInfo && (checkpointInfo.completed > 0 || checkpointInfo.failed > 0);

  return (
    <Card>
      <Card.Header className="fw-semibold bg-light d-flex justify-content-between align-items-center">
        <span>Batch Run — All Staff</span>
        {runId && (
          <small className="text-muted" style={{ fontFamily: 'monospace' }}>
            Run ID: {runId}
          </small>
        )}
      </Card.Header>
      <Card.Body>
        <Alert variant="info" className="mb-3">
          <strong>Note:</strong> CSP and CPI assumptions are read directly from the
          database - Batch Run - Takes approximately 20 Minutes.
        </Alert>

        {status === 'idle' && (
          <>
            {checkpointLoading && (
              <div className="text-muted small mb-3">
                <Spinner size="sm" animation="border" className="me-2" />
                Checking for a previous run…
              </div>
            )}

            {!checkpointLoading && (
              <Card className="mb-3 border-secondary-subtle">
                <Card.Body className="py-3">
                  <div className="fw-semibold small mb-2">Run mode</div>

                  {hasCheckpoint ? (
                    <Alert variant="warning" className="py-2 px-3 small mb-3">
                      A previous run left a checkpoint:{' '}
                      <strong>{checkpointInfo.completed.toLocaleString()}</strong> staff already completed
                      {checkpointInfo.failed > 0 && (
                        <>, <strong className="text-danger">{checkpointInfo.failed.toLocaleString()}</strong> failed last time</>
                      )}.
                    </Alert>
                  ) : (
                    <div className="text-muted small mb-3">
                      No previous run detected for this year — this will be a full run.
                    </div>
                  )}

                  <Form.Check
                        type="radio"
                        name="runMode"
                        id="run-mode-resume"
                        label={
                          <span>
                            <strong>Resume</strong> — skip staff already completed, retry only what's left
                          </span>
                        }
                        checked={runMode === 'resume'}
                        onChange={() => setRunMode('resume')}
                        disabled={!hasCheckpoint}
                        className="mb-2"
                      />
                  <Form.Check
                    type="radio"
                    name="runMode"
                    id="run-mode-fresh"
                    label={
                      <span>
                        <strong>Fresh start</strong> — ignore any previous progress and regenerate every staff member
                      </span>
                    }
                    checked={runMode === 'fresh'}
                    onChange={() => setRunMode('fresh')}
                  />
                </Card.Body>
              </Card>
            )}

            <Form.Check
              type="checkbox"
              id="confirm-batch"
              label={
                runMode === 'fresh'
                  ? 'I confirm I want to run the calculator for ALL staff. This will overwrite existing output files.'
                  : 'I confirm I want to resume the batch run, processing only staff not already completed.'
              }
              checked={confirmed}
              onChange={(e) => setConfirmed(e.target.checked)}
              className="mb-3"
            />
            <Button
              variant="success"
              size="lg"
              onClick={startBatch}
              disabled={!confirmed || checkpointLoading}
            >
              ▶ {runMode === 'fresh' ? 'Run Batch Calculator (Fresh)' : 'Resume Batch Calculator'}
            </Button>
            <div className="text-muted small mt-2">
              Estimated time: 5–15 minutes depending on server speed. Do not close this tab during the run.
            </div>
          </>
        )}

        {status === 'running' && (
          <>
            <div className="mb-2 d-flex justify-content-between align-items-center">
              <span className="fw-semibold text-primary">
                {runMode === 'fresh' ? 'Running (fresh)…' : 'Resuming…'}
              </span>
              <span className="text-muted small">
                {progress.processed.toLocaleString()} / {progress.total.toLocaleString()} staff
              </span>
            </div>
            <ProgressBar
              animated
              now={progress.pct}
              label={`${progress.pct}%`}
              className="mb-3"
              style={{ height: 24 }}
            />
            <Button variant="outline-danger" size="sm" onClick={stopRun} className="mb-3">
              ✕ Cancel
            </Button>
          </>
        )}

        {status === 'done' && (
          <Alert variant="success" className="mb-3">
            ✓ Batch run completed — {progress.processed.toLocaleString()} staff processed
            {runId && (
              <div className="mt-1">
                <small>
                  View full details in{' '}
                  <a href={`/admin/sps/run-history`}>Run History</a>
                </small>
              </div>
            )}
          </Alert>
        )}

        {status === 'error' && (
          <Alert variant="danger" className="mb-3">
            Run ended with errors. Progress up to the failure point has been checkpointed —
            you can re-open this screen and choose <strong>Resume</strong> to continue.
            Check the log below and{' '}
            <a href="/admin/sps/run-history">Run History</a> for details.
          </Alert>
        )}

        {/* Log window */}
        {log.length > 0 && (
          <div
            ref={logRef}
            style={{
              height:     320,
              overflowY:  'auto',
              background: '#f8f9fa',
              border:     '1px solid #dee2e6',
              borderRadius: 4,
              padding:    '8px 10px',
            }}
          >
            {log.map((entry, i) => (
              <div key={i} style={logLineStyle(entry.type)}>
                {entry.line}
              </div>
            ))}
          </div>
        )}
      </Card.Body>
    </Card>
  );
}


// ── Main screen ───────────────────────────────────────────────────────────────
export default function RunCalculatorScreen() {
  return (
    <Container fluid className="py-3">
      <Row className="mb-3">
        <Col>
          <h4 className="mb-0">Run SPS Calculator</h4>
          <small className="text-muted">
            Generate Excel certificates and HTML statements for staff
          </small>
        </Col>
      </Row>
      <Row>
        <Col xl={7} className="mb-4">
          <IndividualRunCard />
          <BatchRunCard />
        </Col>
        <Col xl={5}>
          <Card className="border-0 bg-light h-100">
            <Card.Body>
              <h6 className="fw-semibold mb-3">Three files are produced per staff member</h6>
              <ol className="small text-muted ps-3">
                <li className="mb-2">
                  <strong className="text-dark">Excel Certificate (.xlsx)</strong><br />
                  Monthly or fortnightly sheet with per-period contributions,
                  referable amounts, CPI chain, and leaver statement.
                </li>
                <li className="mb-2">
                  <strong className="text-dark">CPI Summary (.html)</strong><br />
                  Personalised §4 and §5 of the SPS CPI Guide — worked example
                  table and four interactive charts.
                </li>
                <li className="mb-2">
                  <strong className="text-dark">Annual Benefit Statement (.html)</strong><br />
                  Full ABS matching the official template — Parts A through I,
                  with contributions and referable amount tables filled in.
                </li>
              </ol>
              <hr />
              <h6 className="fw-semibold mb-2">Email Centre</h6>
              <p className="small text-muted mb-0">
                Files produced can be downloaded individually or emailed to staff
                from the Email Centre screen.
              </p>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
}